<template>
    <v-app :class="{ blur: showCredits, mobile: smAndDown }" :theme="store.theme" fluid>
        <v-navigation-drawer v-if="!smAndDown" order="2" width="200" floating location="left">
            <div class="h-100 d-flex align-center"></div>
        </v-navigation-drawer>
        <nav-header v-if="routerIsReady" :theme="store.theme" @update:theme="updateTheme" :signup="signup" />
        <v-main>
            <router-view v-slot="{ Component }">
                <keep-alive>
                    <component :is="Component" @update:theme="updateTheme" @error="errorHandler" @signup="signup = true" />
                </keep-alive>
            </router-view>
        </v-main>
        <v-navigation-drawer v-if="!smAndDown" order="2" width="200" floating location="right">
            <div class="h-100 d-flex align-center"></div>
        </v-navigation-drawer>
        <nav-footer />
        <v-snackbar text :timeout="-1" v-model="snackbar.active" style="opacity: 0.95" @click="snackbarCloseHandler" :color="store.theme === 'dark' ? 'grey-lighten-4' : 'grey-darken-4'">
            <v-row>
                <v-col cols="1" class="d-flex align-center justify-center py-0">
                    <v-icon :icon="snackbar.icon" :color="snackbar.iconColor" :size="smAndDown ? 'x-small' : 'x-large'" class="ml-2" />
                </v-col>
                <v-col cols="10" class="d-flex align-center justify-center py-0">
                    <span v-if="snackbar.message" v-html="snackbar.message"></span>
                    <span v-else @click="reload" class="font-weight-light" v-bind:class="smAndDown ? 'caption' : ''" style="cursor: pointer">App update available.</span>
                </v-col>
                <v-col cols="1" class="d-flex align-center justify-center py-0">
                    <v-btn variant="plain" :size="smAndDown ? 'x-small' : 'default'" @click="snackbarCloseHandler" icon="cancel" />
                </v-col>
            </v-row>
        </v-snackbar>
    </v-app>
</template>
<script setup>
import { ref, getCurrentInstance, provide } from 'vue'
import { useAppStore } from '@/store/app'
import { useDisplay } from 'vuetify/lib/framework.mjs'
import posthog from 'posthog-js'
import 'animate.css'

import NavHeader from '@/components/nav/NavHeader.vue'
import NavFooter from './components/nav/NavFooter.vue'

posthog.init('phc_MYdJD4PZck0m9cXqUzroogexS0mtZa6LbHjobl4fDYD',
    {
        api_host: 'https://us.i.posthog.com',
        person_profiles: 'always'
    }
)
const { $api, $keycloak } = getCurrentInstance().appContext.config.globalProperties
const { smAndDown } = useDisplay()
const store = useAppStore()
const resetLocalStorage = ref(false)
const routerIsReady = ref(false)
const signup = ref(false)
const emit = defineEmits(['update:theme', 'error', 'signup'])
const error = ref(false)
const version = ref()
const snackbarDefault = {
    active: false,
    icon: 'info',
    message: undefined,
}
const snackbar = ref({ ...snackbarDefault })
const lastBuild = ref()
const showCredits = ref(false)
const versionCheckIntervalId = ref()
const buildInfo = ref()

function errorHandler(errorMessage) {
    error.value = true
    snackbar.value = {
        active: true,
        icon: 'error',
        iconColor: 'red',
        message: errorMessage,
    }
}
function snackbarCloseHandler() {
    snackbar.value.active = false
    setTimeout(() => {
        snackbar.value = { ...snackbarDefault }
        if (error.value) {
            error.value = false
        }
    }, 500)
}
function reload() {
    const url = new URL(window.location.href)
    url.searchParams.set('cache', Date.now())
    window.location.href = url.toString()
}
async function checkVersion() {
    buildInfo.value = await $api.buildInfo()

    if (!buildInfo.value?.build_date) {
        return
    }
    version.value = buildInfo.value.commit_sha
    if (lastBuild.value && lastBuild.value?.build_date !== buildInfo.value.build_date) {
        snackbar.value.active = true
        // TODO: put some extra check here...
        resetLocalStorage.value = true
    } else {
        lastBuild.value = buildInfo.value
    }
}
function cacheIsOld(timestamp = Date.now(), maxAge = store.apiCalls.maxAge) {
    const expires = timestamp + maxAge
    const isOld = !timestamp || Date.now() > expires

    const red = '\x1b[31m'
    const green = '\x1b[32m'
    const reset = '\x1b[0m'
    console.log(`cache (${isOld ? `${red}EXPIRED${reset}` : `${green}valid${reset}`}): timestamp:${new Date(timestamp).toLocaleString()}, expires: ${new Date(expires).toLocaleString()} (${(expires - Date.now()) / 1000}) secs`)
    return isOld
}
async function hashString(message, algorithm = 'SHA-1') {
    const encoder = new TextEncoder()
    const data = encoder.encode(message)
    const hashBuffer = await crypto.subtle.digest(algorithm, data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
}
function updateTheme() {
    store.theme = store.theme === 'dark' ? 'light' : 'dark'
    emit('update:theme')
}
function debouncer(func, delay) {
    let debounceTimeout

    return (...args) => {
        if (debounceTimeout) clearTimeout(debounceTimeout)
        debounceTimeout = setTimeout(() => {
            func.apply(this, args)
        }, delay)
    }
}
function cleanPayload(data) {
    const payload = Object.fromEntries(Object.entries({
        ...data,
    }).filter(([_key, value]) => value))

    return payload
}
async function copyHandler(tooltips, tooltipName, value) {
    try {
        const copied = await clipboard.copy(value)
        if (copied && !(copied instanceof Error)) {
            tooltips[tooltipName] = true
        }
    } finally {
        setTimeout(() => {
            tooltips[tooltipName] = false
        }, 1500)
    }
}

checkVersion()
versionCheckIntervalId.value = setInterval(checkVersion, 60000)

provide('copyHandler', copyHandler)
provide('cleanPayload', cleanPayload)
provide('debouncer', debouncer)
provide('hashString', hashString)
provide('cacheIsOld', cacheIsOld)
provide('idToken', () => $keycloak.idTokenParsed)
</script>
